import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Compass,
  Download,
  FileText,
  XCircle,
} from 'lucide-react';
import { apiUrl } from '../../services/apiClient';
import { formatAnswerValue, formatFieldLabel } from '../../utils/format';

export function getOutcomeVisuals(outcome) {
  if (!outcome) return null;
  if (outcome.outcome === 'submitted') {
    return {
      Icon: CheckCircle2,
      surface: 'var(--surface-green)',
      color: 'var(--text-green)',
      label: 'Submitted',
    };
  }
  if (outcome.outcome === 'redirect') {
    return {
      Icon: Compass,
      surface: 'var(--surface-blue)',
      color: 'var(--text-blue)',
      label: 'Redirect',
    };
  }
  if (outcome.outcome === 'stop' || outcome.severity === 'critical') {
    return {
      Icon: XCircle,
      surface: 'var(--surface-red)',
      color: 'var(--text-critical)',
      label: 'Hard stop',
    };
  }
  if (outcome.outcome === 'proceed') {
    return {
      Icon: CheckCircle2,
      surface: 'var(--surface-green)',
      color: 'var(--text-green)',
      label: 'Proceeded',
    };
  }
  return {
    Icon: AlertTriangle,
    surface: 'var(--surface-yellow)',
    color: 'var(--text-yellow)',
    label: 'Needs attention',
  };
}

function OutcomeSectionTitle({ children, style }) {
  return (
    <div
      className="muted"
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 0,
        marginBottom: 8,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function OutcomeSectionCard({ title, children, style, titleStyle }) {
  return (
    <div className="card" style={{ padding: 18, marginBottom: 12, ...style }}>
      {title && <OutcomeSectionTitle style={titleStyle}>{title}</OutcomeSectionTitle>}
      {children}
    </div>
  );
}

export function OutcomeHeader({ outcome, headingLevel = 2, style, headingStyle }) {
  const visual = getOutcomeVisuals(outcome);
  if (!outcome || !visual) return null;
  const { Icon, surface, color, label } = visual;
  const Heading = headingLevel === 1 ? 'h1' : 'h2';

  return (
    <div className="row" style={{ gap: 14, alignItems: 'flex-start', marginBottom: 18, ...style }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          background: surface,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="muted"
          style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0, marginBottom: 4 }}
        >
          {outcome.gate ? `${label} · ${outcome.gate}` : label}
        </div>
        <Heading
          className="page-title"
          style={{ marginBottom: 6, fontSize: headingLevel === 1 ? 22 : 20, ...headingStyle }}
        >
          {outcome.summary}
        </Heading>
        <div className="page-subtitle" style={{ fontSize: 14 }}>
          {outcome.message}
        </div>
      </div>
    </div>
  );
}

export function OutcomeContactCard({ outcome, label, style }) {
  if (!outcome?.contact) return null;
  const title = label || (outcome.outcome === 'submitted' ? 'Review owner' : 'Escalate to');

  return (
    <OutcomeSectionCard title={title} titleStyle={{ marginBottom: 4 }} style={style}>
      <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>
        {outcome.contact}
      </div>
    </OutcomeSectionCard>
  );
}

function outcomeFlags(outcome) {
  if (!outcome) return [];
  if (outcome.flagDetails?.length) return outcome.flagDetails;
  return (outcome.flags || []).map((id) => ({ id }));
}

export function OutcomeFlagsCard({ outcome, style }) {
  const flags = outcomeFlags(outcome);
  if (flags.length === 0) return null;

  return (
    <OutcomeSectionCard title="Flags" titleStyle={{ marginBottom: 10 }} style={style}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {flags.map((flag) => (
          <div key={flag.id || flag.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {flag.label && (
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                  {flag.label}
                </span>
              )}
              <span className="chip gray code">{flag.id}</span>
            </div>
            {flag.description && (
              <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
                {flag.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </OutcomeSectionCard>
  );
}

export function OutcomeWhyCard({ outcome, style }) {
  const path = outcome?.path || [];
  const citations = outcome?.citations || [];
  if (path.length === 0 && citations.length === 0) return null;

  return (
    <OutcomeSectionCard title="Why this outcome" titleStyle={{ marginBottom: 10 }} style={style}>
      {path.length > 0 && (
        <div style={{ marginBottom: citations.length > 0 ? 12 : 0 }}>
          <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
            Decision path
          </div>
          <div className="row" style={{ gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            {path.map((gate, index) => (
              <React.Fragment key={`${gate}-${index}`}>
                {index > 0 && (
                  <ArrowRight
                    className="w-3 h-3"
                    style={{ color: 'var(--icon-tertiary)', flexShrink: 0 }}
                  />
                )}
                <span className="chip code">{gate}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      {citations.length > 0 && (
        <div>
          <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
            Based on the prospect's answers
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(160px, 220px) minmax(0, 1fr)',
              gap: '6px 10px',
              fontSize: 12,
            }}
          >
            {citations.map((citation) => (
              <React.Fragment key={citation.field}>
                <span className="muted">{formatFieldLabel(citation.field)}</span>
                <span className="mono" style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                  {formatAnswerValue(citation.value)}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </OutcomeSectionCard>
  );
}

export function OutcomeNextStepsCard({ outcome, style }) {
  const steps = outcome?.nextSteps || [];
  if (steps.length === 0) return null;

  return (
    <OutcomeSectionCard title="Suggested next steps" style={style}>
      <ul
        style={{
          paddingLeft: 0,
          margin: 0,
          fontSize: 13,
          lineHeight: 1.6,
          color: 'var(--text-primary)',
          listStyle: 'none',
        }}
      >
        {steps.map((step, index) => (
          <li key={index} style={{ marginBottom: index < steps.length - 1 ? 4 : 0 }}>
            {step}
          </li>
        ))}
      </ul>
    </OutcomeSectionCard>
  );
}

export function GeneratedDraftsCard({ contracts, style }) {
  if (!contracts?.length) return null;

  return (
    <OutcomeSectionCard title={`Generated drafts (${contracts.length})`} titleStyle={{ marginBottom: 10 }} style={style}>
      <div style={{ display: 'grid', gap: 8 }}>
        {contracts.map((contract) => (
          <div
            key={contract.id}
            className="row"
            style={{
              gap: 10,
              alignItems: 'center',
              padding: '8px 10px',
              background: 'var(--surface-alpha-primary)',
              borderRadius: 6,
            }}
          >
            <FileText className="w-4 h-4" style={{ color: 'var(--icon-tertiary)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
              {contract.title || contract.short_name || contract.id}
            </div>
            <Link to={`/contracts/${contract.id}`} className="btn outline sm">
              Open
            </Link>
            {contract.latestFileId && (
              <a
                href={apiUrl(`/contracts/${contract.id}/files/${contract.latestFileId}/download`)}
                download
                className="btn ghost sm"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </a>
            )}
          </div>
        ))}
      </div>
    </OutcomeSectionCard>
  );
}

export function CapturedAnswersCard({ history, style }) {
  if (!history?.length) return null;

  return (
    <OutcomeSectionCard title="Captured answers" titleStyle={{ marginBottom: 10 }} style={style}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(160px, 220px) minmax(0, 1fr)',
          gap: '8px 12px',
          fontSize: 13,
        }}
      >
        {history.map((answer) => (
          <React.Fragment key={answer.field}>
            <span className="muted">{formatFieldLabel(answer.field)}</span>
            <span className="mono" style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>
              {formatAnswerValue(answer.value)}
            </span>
          </React.Fragment>
        ))}
      </div>
    </OutcomeSectionCard>
  );
}
